// ============================================================================
// Achievements — definição de conquistas por cargo (estilo elo de LoL)
// ============================================================================
// Cada categoria tem múltiplos tiers em ordem crescente. O profissional
// desbloqueia o maior tier que alcançou no fechamento do mês.
//
// Tiers visuais (mapeados pela posição relativa do tier dentro da categoria):
//   0-19%: bronze        | bordô, cobre
//   20-39%: silver       | prata
//   40-59%: gold         | dourado
//   60-79%: platinum     | turquesa
//   80-94%: diamond      | azul claro
//   95-100%: master      | gradiente vermelho/roxo
// ============================================================================

import { DollarSign, Award, Target, Users, Package, Layers, Scissors } from 'lucide-react';

// Versão do schema dos tiers — incrementa quando muda valores/categorias.
// Quando o app detecta versão diferente da salva no localStorage, RESETA
// achievements, notifications e processed pra reprocessar tudo do zero.
//
// v4 (01/05/2026): Conquistas agora só são avaliadas no ÚLTIMO DIA do mês.
//   Bump força reset pra apagar tiers liberados prematuramente em meses
//   correntes anteriores (ex: alguém destravou Bronze Ticket no dia 1
//   com 1 atendimento). Após reset, rodam só meses fechados + corrente
//   APENAS se hoje for o último dia.
export const ACHIEVEMENTS_SCHEMA_VERSION = 4;

// ─── Validade das conquistas ─────────────────────────────────────────────────
// Conquistas valem por 6 meses corridos. Se o profissional NÃO repetir o
// desempenho daquele tier dentro da janela de 6 meses, a conquista vai pra
// "histórico" (perdida) e a borda do avatar regride. Quando ele bater de
// novo o tier, ela "volta a brilhar" (reconquistada).
//
// Implementação: tier ATIVO = max tier alcançado em qualquer mês fechado dos
// últimos N meses. Histórico = todos os tiers já alcançados algum dia.
// Tiers no histórico mas não no ativo = "perdidos" (visíveis na aba Histórico).
export const ACHIEVEMENT_VALIDITY_MONTHS = 6;

// Roles e suas categorias de conquista
// 7 tiers por categoria (Barbeiro): 1:1 com bronze→prata→ouro→platina→diamante→mestre→desafiante
export const ACHIEVEMENT_CATEGORIES = {
  Barbeiro: [
    { id: 'fat', label: 'Faturamento', icon: DollarSign, format: 'currency', metric: 'faturamento',
      tiers: [20000, 25000, 30000, 35000, 40000, 45000, 50000] },
    { id: 'com', label: 'Comissão', icon: Award, format: 'currency', metric: 'comissao',
      tiers: [7000, 10000, 12000, 15000, 16000, 20000, 25000] },
    { id: 'tkt', label: 'Ticket Médio', icon: Target, format: 'currency', metric: 'ticketMedio',
      tiers: [200, 240, 260, 280, 300, 400, 450] },
    { id: 'pref', label: 'Clientes Preferenciais', icon: Users, format: 'number', metric: 'agendamentosPreferenciais',
      tiers: [80, 100, 150, 170, 190, 210, 230] },
    { id: 'prod', label: 'Produtos Vendidos', icon: Package, format: 'number', metric: 'produtosVendidos',
      tiers: [15, 20, 25, 30, 35, 40, 50] },
  ],
  Recepcionista: [
    { id: 'fatpac', label: 'Faturamento de Pacotes', icon: Layers, format: 'currency', metric: 'faturamentoPacotes',
      tiers: [5000, 10000, 15000, 20000, 25000, 30000] },
    { id: 'qtdpac', label: 'Quantidade de Pacotes', icon: Package, format: 'number', metric: 'pacotesVendidos',
      tiers: [15, 20, 25, 30, 35, 40, 45, 50] },
  ],
  Assistente: [
    { id: 'fat', label: 'Faturamento', icon: DollarSign, format: 'currency', metric: 'faturamento',
      tiers: [2000, 4000, 6000, 8000, 10000, 12000, 14000, 15000, 17000] },
    { id: 'svc', label: 'Quantidade de Serviços', icon: Scissors, format: 'number', metric: 'servicosRealizados',
      tiers: [40, 50, 60, 70, 80, 90, 100, 120, 140] },
  ],
};

// Helper: pega categorias para um role
export function getCategoriesForRole(role) {
  return ACHIEVEMENT_CATEGORIES[role] || [];
}

// Helper: pega categoria específica
export function getCategory(role, categoryId) {
  return (ACHIEVEMENT_CATEGORIES[role] || []).find(c => c.id === categoryId);
}

// Mapeia tier index → faixa visual.
// Quando a categoria tem 7 tiers (caso Barbeiro), mapeamento é 1:1.
// Pra outras categorias, distribui proporcionalmente.
const RANK_ORDER = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master', 'challenger'];
export function getTierRank(tierIndex, totalTiers) {
  if (totalTiers <= 0) return 'bronze';
  // Mapeamento 1:1 quando exatamente 7 tiers (Barbeiro)
  if (totalTiers === 7) return RANK_ORDER[Math.min(tierIndex, 6)];
  // Proporcional pras outras (Recepcionista=6/8 tiers, Assistente=9 tiers)
  const pct = ((tierIndex + 1) / totalTiers) * 100;
  if (pct >= 100) return 'challenger';
  if (pct >= 87) return 'master';
  if (pct >= 72) return 'diamond';
  if (pct >= 55) return 'platinum';
  if (pct >= 40) return 'gold';
  if (pct >= 22) return 'silver';
  return 'bronze';
}

// ─── ESTILOS VISUAIS DOS RANKS ───────────────────────────────────────────
// Gradientes complexos com 4-5 stops pra dar profundidade real (não-IA-feeling).
// Cada rank tem: rim metálico (ringColor), gradiente principal (gradient),
// gradiente interno (bgGradient), glow externo, e cor de acento (accent).
// O `accent` é usado pra sparkles, gemas, partículas — adiciona contraste visual.
export const RANK_STYLES = {
  bronze: {
    color: '#B87333',
    gradient: 'linear-gradient(145deg, #5C3A1E 0%, #8B5A2B 25%, #CD7F32 50%, #A0522D 75%, #6B4423 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(255,210,160,0.4) 0%, rgba(184,115,51,0.35) 50%, rgba(80,45,15,0.6) 100%)',
    glow: '0 0 22px rgba(184,115,51,0.55), 0 0 40px rgba(80,45,15,0.3)',
    text: '#FFE4C4',
    ringColor: '#8B5A2B',
    accent: '#FFD9A8',
    highlight: 'rgba(255,210,160,0.5)',
  },
  silver: {
    color: '#D5D5DD',
    gradient: 'linear-gradient(145deg, #5A5A66 0%, #9CA3B0 25%, #E8E8F0 50%, #B8BCC8 75%, #7A7E8A 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(245,247,255,0.45) 0%, rgba(192,196,210,0.3) 50%, rgba(74,78,90,0.55) 100%)',
    glow: '0 0 24px rgba(213,217,225,0.6), 0 0 45px rgba(140,148,170,0.3)',
    text: '#F8FAFF',
    ringColor: '#9CA3B0',
    accent: '#E0E5F2',
    highlight: 'rgba(255,255,255,0.6)',
  },
  gold: {
    color: '#FFD700',
    gradient: 'linear-gradient(145deg, #6B4F0A 0%, #B8860B 22%, #FFD700 50%, #FFE564 70%, #C99500 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(255,250,180,0.55) 0%, rgba(255,215,0,0.35) 50%, rgba(107,79,10,0.6) 100%)',
    glow: '0 0 28px rgba(255,215,0,0.7), 0 0 55px rgba(255,179,0,0.4)',
    text: '#FFFEE0',
    ringColor: '#B8860B',
    accent: '#FFF8C8',
    highlight: 'rgba(255,250,180,0.7)',
  },
  platinum: {
    color: '#62E5D9',
    gradient: 'linear-gradient(145deg, #0F4D4A 0%, #1FA395 22%, #62E5D9 50%, #A0F4EE 70%, #2BA89A 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(180,250,240,0.55) 0%, rgba(98,229,217,0.35) 50%, rgba(15,77,74,0.6) 100%)',
    glow: '0 0 28px rgba(98,229,217,0.7), 0 0 55px rgba(31,163,149,0.4)',
    text: '#E0FFFA',
    ringColor: '#1FA395',
    accent: '#B4FAF0',
    highlight: 'rgba(200,255,250,0.65)',
  },
  diamond: {
    color: '#7BD8FF',
    gradient: 'linear-gradient(145deg, #0E3A6C 0%, #1E68C9 20%, #5BB8FF 45%, #BCE8FF 60%, #7BD8FF 80%, #1A4D8C 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(220,240,255,0.6) 0%, rgba(123,216,255,0.4) 50%, rgba(14,58,108,0.65) 100%)',
    glow: '0 0 32px rgba(123,216,255,0.8), 0 0 60px rgba(30,104,201,0.45), 0 0 90px rgba(220,240,255,0.2)',
    text: '#EAF6FF',
    ringColor: '#1E68C9',
    accent: '#DDF2FF',
    highlight: 'rgba(220,240,255,0.75)',
  },
  master: {
    color: '#E84B7C',
    gradient: 'linear-gradient(145deg, #4A1240 0%, #B91C5C 22%, #E84B7C 45%, #C25CE8 65%, #7C3AED 85%, #2D1B4E 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(255,200,225,0.55) 0%, rgba(232,75,124,0.4) 40%, rgba(124,58,237,0.45) 70%, rgba(45,27,78,0.65) 100%)',
    glow: '0 0 36px rgba(232,75,124,0.8), 0 0 60px rgba(168,85,247,0.55), 0 0 100px rgba(124,58,237,0.3)',
    text: '#FFE0EE',
    ringColor: '#E84B7C',
    accent: '#FFCCE0',
    highlight: 'rgba(255,200,225,0.7)',
  },
  challenger: {
    // Vermelho-fogo + dourado: tier supremo
    color: '#FF4757',
    gradient: 'linear-gradient(145deg, #2B0606 0%, #8B0000 18%, #DC143C 38%, #FF4757 55%, #FF8C42 72%, #FFD700 88%, #B8860B 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 22%, rgba(255,220,170,0.65) 0%, rgba(255,71,87,0.45) 35%, rgba(220,20,60,0.5) 65%, rgba(43,6,6,0.7) 100%)',
    glow: '0 0 40px rgba(255,71,87,0.85), 0 0 70px rgba(255,140,66,0.55), 0 0 110px rgba(255,215,0,0.35)',
    text: '#FFEEDD',
    ringColor: '#DC143C',
    accent: '#FFD700',
    highlight: 'rgba(255,235,180,0.8)',
  },
  locked: {
    color: '#9CA3AF',
    gradient: 'linear-gradient(135deg, #2A2D33 0%, #4B5563 50%, #2A2D33 100%)',
    bgGradient: 'radial-gradient(ellipse at 30% 25%, rgba(120,124,135,0.2) 0%, rgba(75,85,99,0.15) 50%, rgba(20,22,28,0.5) 100%)',
    glow: 'none',
    text: '#D1D5DB',
    ringColor: '#4B5563',
    accent: '#6B7280',
    highlight: 'rgba(255,255,255,0.05)',
  },
};

// Mapeamento de nomes (uso interno, não exibido nos badges)
export const RANK_NAMES = {
  bronze: 'Bronze',
  silver: 'Prata',
  gold: 'Ouro',
  platinum: 'Platina',
  diamond: 'Diamante',
  master: 'Mestre',
  challenger: 'Desafiante',
  locked: 'Bloqueada',
};
